import { useContext, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import resolveScope from '../../utils/redux/#resolveScope';
import mapScopeToProps from '../../utils/redux/mapScopeToProps';

import get from '../../utils/accessors/get';

import ScopeContext from '../../components/redux/#ScopeContext';

type Fn<SelectedValue, ScopedState> = (scopedState: ScopedState) => SelectedValue;
type FnOrPath<SelectedValue, ScopedState> = string | Fn<SelectedValue, ScopedState>;

export interface UseScopedSelectorMock {
  mockState: (state: Record<string, any>) => void;
  state: Record<string, any>;
}

const useScopedSelector = <
  SelectedValue,
  ScopedState extends object = {},
>(
    fnOrPath: FnOrPath<SelectedValue, ScopedState>,
    defaultValue?: SelectedValue,
    fn?: Fn<SelectedValue, ScopedState>,
  ): SelectedValue => {
  const { scope } = useContext(ScopeContext);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const cachedDefaultValue = useMemo(() => defaultValue, []);

  const selector = useCallback((state: object) => {
    if (typeof fnOrPath === 'object') {
      return mapScopeToProps(fnOrPath)(state, { scope });
    }

    if (typeof fnOrPath === 'function') {
      const scopedState = get(state, scope) as ScopedState;
      return fnOrPath(scopedState);
    }

    if (fn) {
      const resolvedPath = resolveScope(fnOrPath, scope);
      const scopedState = get(state, resolvedPath) as ScopedState;
      return fn(scopedState);
    }

    const resolvedPath = resolveScope(fnOrPath, scope);

    return get(state, resolvedPath);
  }, [fnOrPath, fn, scope]);

  const value = useSelector(selector);

  if (value !== undefined) {
    return value;
  }

  return cachedDefaultValue;
};

/**
 * @visibleName useScopedSelector
 * @component
 */
export default useScopedSelector;
